import { ConditionPickerOption, Conditions, ConditionDataType, ConditionOperationType } from './conditions';
import { ConditionViewModel } from './conditionViewModel';
import { ContactAttributeViewModel } from '@/messaging/models/ContactAttributeViewModel';
import AttributeIconUtil from '@/shared/util/attributeIconUtil';

export interface IConditionsManager {
	conditions: ConditionPickerOption[];
	visibleConditions: ConditionPickerOption[];
	getConfigComponent(condition: ConditionViewModel): string;
	getConditionOptions(condition: ConditionViewModel): any;
	getFormattedDescription(condition: ConditionViewModel): string;
	getListConditionValueTitle(condition: ConditionViewModel): string;
	getIntentConditionValueTitle(condition: ConditionViewModel): string;
	isConditionValid(condition: ConditionViewModel): boolean;
}

export abstract class ConditionsManager implements IConditionsManager {
	public operations = Conditions.Operations;
	public dateTimeUnits = Conditions.DateTimeUnits;

	constructor(private cachedAttributes: ContactAttributeViewModel[]) {
	}

	public get visibleConditions(): ConditionPickerOption[] {
		return this.conditions;
	}

	public get conditions(): ConditionPickerOption[] {
		return this.conditionsFromCache;
	}

	public getConfigComponent(condition: ConditionViewModel): string {
		if (condition.type === ConditionDataType.DateTime || condition.type === ConditionDataType.Date) {
			return 'DateTimeCondition';
		} else if (condition.type === ConditionDataType.Text) {
			return 'TextCondition';
		} else if (condition.type === ConditionDataType.Number) {
			return 'NumberCondition';
		} else if (condition.type === ConditionDataType.Decimal) {
			return 'DecimalCondition';
		} else if (condition.type === ConditionDataType.List) {
			return 'ListCondition';
		} else if (condition.type === ConditionDataType.Duration) {
			return 'DurationCondition';
		} else if (condition.type === ConditionDataType.Intent) {
			return 'IntentCondition';
		}
		return '';
	}

	public getConditionOptions(condition: ConditionViewModel): any {
		return '';
	}

	public getListConditionValueTitle(condition: ConditionViewModel) {
		return condition.value;
	}

	public getIntentConditionValueTitle(condition: ConditionViewModel) {
		return condition.value;
	}

	public isConditionValid(condition: ConditionViewModel): boolean {
		if (condition.attribute.startsWith('appUser.customAttributes')) {
			const attributeName = condition.attribute.substring(condition!.attribute.lastIndexOf('.') + 1);
			const attribute = this.cachedAttributes.find(attr => attr.id === attributeName);
			return !condition.invalid && !!attribute && !attribute.archived;
		}
		return !condition.invalid;
	}

	public getFormattedDescription(condition: ConditionViewModel) {
		if (condition.invalid) {
			return `${this.getAttribute(condition)} is invalid`.trim();
		}
		return `${this.getAttribute(condition)} ${this.getOperation(condition)} ${this.getValue(condition)} ${this.getUnit(condition)}`.trim();
	}

	protected get conditionsFromCache(): ConditionPickerOption[] {
		return this.cachedAttributes.map(attribute => ({
			id: this.getId(attribute),
			title: attribute.name,
			type: attribute.type,
			icon: AttributeIconUtil.getIcon(attribute.type, attribute.id, attribute.system)
		}));
	}

	private getAttribute(condition: ConditionViewModel) {
		const matchedAttribute = this.conditions.find(c => c.id === condition.attribute);
		if (matchedAttribute) {
			return matchedAttribute.title;
		} else if (condition.attribute.includes('.')) {
			return condition.attribute.substring(condition.attribute.lastIndexOf('.') + 1);
		}

		return condition.attribute;
	}

	private getOperation(condition: ConditionViewModel) {
		const typedOperations = this.operations[condition.type];
		if (!typedOperations) {
			return '...';
		}

		const matchedOperation = typedOperations.find(o => o.operation === condition.operation && o.result === condition.result);
		if (!matchedOperation) {
			return '...';
		}

		return matchedOperation.title;
	}

	private getValue(condition: ConditionViewModel) {
		if (condition.type === ConditionDataType.List) {
			return this.getListConditionValueTitle(condition);
		} else if (condition.type === ConditionDataType.Intent) {
			return this.getIntentConditionValueTitle(condition);
		}
		return condition.value;
	}

	private getUnit(condition: ConditionViewModel) {
		if (condition.unit && condition.operation !== ConditionOperationType.IsSet) {
			// 'seconds' is old type
			if (condition.type === ConditionDataType.Duration || condition.type === 'seconds') {
				return condition.unit;
			}

			const dateTime = this.dateTimeUnits.find(u => u.id === condition.unit);
			if (dateTime) {
				let title = dateTime.title;
				if (condition.value === '1') {
					title = dateTime.title.slice(0, dateTime.title.length - 1);
				}
				return `${title} ago`;
			}
		}

		return '';
	}

	private getId(attribute: ContactAttributeViewModel): string {
		if (attribute.system) {
			return `appUser.${attribute.id}`;
		} else {
			return `appUser.customAttributes.${attribute.id}`;
		}
	}
}

